import { 
  Brain, 
  Users, 
  MessageSquare, 
  BookOpen, 
  BarChart3, 
  Target,
  CheckCircle,
  Clock,
  AlertTriangle,
  Lightbulb,
  Cog,
  Heart,
  Zap,
  Shield,
  TrendingUp,
  Award
} from 'lucide-react';

// Diagnostic Assessment Questions
export const changeReadinessQuestions = [
  {
    id: 1,
    category: 'Liderazgo',
    question: '¿Los líderes comunican activamente la visión del cambio?',
    options: ['Nunca', 'Raramente', 'A veces', 'Frecuentemente', 'Siempre']
  },
  {
    id: 2,
    category: 'Liderazgo',
    question: '¿Existe un sponsor ejecutivo visible para las iniciativas de cambio?',
    options: ['No existe', 'Poco visible', 'Moderadamente visible', 'Visible', 'Muy visible']
  },
  {
    id: 3,
    category: 'Cultura',
    question: '¿Cómo reacciona generalmente el equipo ante nuevas tecnologías?',
    options: ['Resistencia total', 'Escepticismo', 'Neutral', 'Aceptación', 'Entusiasmo']
  },
  {
    id: 4,
    category: 'Cultura',
    question: '¿Se fomenta la experimentación y el aprendizaje de errores?',
    options: ['Nunca', 'Raramente', 'A veces', 'Frecuentemente', 'Siempre']
  },
  {
    id: 5,
    category: 'Comunicación',
    question: '¿Existe transparencia en la comunicación sobre cambios organizacionales?',
    options: ['Nula', 'Baja', 'Moderada', 'Alta', 'Muy alta']
  },
  {
    id: 6,
    category: 'Capacitación',
    question: '¿Se invierte en capacitación antes de implementar nuevas herramientas?',
    options: ['Nunca', 'Raramente', 'A veces', 'Frecuentemente', 'Siempre']
  },
  {
    id: 7,
    category: 'Recursos',
    question: '¿Se asignan recursos dedicados para gestionar el cambio?',
    options: ['Ninguno', 'Mínimos', 'Algunos', 'Suficientes', 'Abundantes']
  },
  {
    id: 8,
    category: 'Métricas',
    question: '¿Se mide el impacto y adopción de los cambios implementados?',
    options: ['Nunca', 'Raramente', 'A veces', 'Frecuentemente', 'Siempre']
  }
];

// Integrated Kotter-ADKAR Framework
export const kotterAdkarPhases = [
  {
    id: 1,
    phase: 'Preparación',
    description: 'Crear las condiciones para que el cambio sea posible',
    color: 'from-amber-500 to-orange-500',
    kotterSteps: [
      {
        step: 1,
        title: 'Crear Urgencia',
        description: 'Desarrollar un sentido de urgencia alrededor de la necesidad de cambio',
        icon: 'AlertTriangle',
        actions: [
          'Identificar amenazas potenciales y escenarios futuros',
          'Examinar oportunidades que deberían explotarse',
          'Iniciar debates honestos sobre la situación actual',
          'Solicitar apoyo de stakeholders clave'
        ],
        tools: ['Análisis FODA', 'Mapeo de Stakeholders', 'Escenarios Futuros'],
        status: 'completed'
      },
      {
        step: 2,
        title: 'Formar Coalición',
        description: 'Crear un equipo de líderes influyentes que guíen el cambio',
        icon: 'Users',
        actions: [
          'Identificar líderes de cambio en la organización',
          'Construir equipo con representación diversa',
          'Asegurar compromiso emocional del equipo',
          'Evaluar debilidades y asegurar mezcla de habilidades'
        ],
        tools: ['Matriz de Influencia', 'Team Charter', 'RACI Matrix'],
        status: 'in_progress'
      }
    ],
    adkarFocus: {
      letter: 'A',
      name: 'Awareness',
      nameEs: 'Conciencia',
      description: 'Crear conciencia de por qué el cambio es necesario',
      keyQuestion: '¿Por qué necesitamos cambiar?',
      activities: [
        'Comunicar la situación actual vs. deseada',
        'Explicar riesgos de no cambiar',
        'Compartir visión del futuro con IA',
        'Presentar casos de éxito del mercado'
      ],
      metrics: ['% que entiende la necesidad', 'Engagement en comunicaciones', 'Preguntas recibidas'],
      score: 85
    },
    progress: 80,
    duration: '2-4 semanas'
  },
  {
    id: 2,
    phase: 'Visión y Estrategia',
    description: 'Definir hacia dónde vamos y cómo lo comunicamos',
    color: 'from-blue-500 to-cyan-500',
    kotterSteps: [
      {
        step: 3,
        title: 'Desarrollar Visión',
        description: 'Crear una visión clara para dirigir el esfuerzo de cambio',
        icon: 'Lightbulb',
        actions: [
          'Determinar valores centrales del cambio',
          'Desarrollar una visión corta y memorable',
          'Crear estrategia para ejecutar la visión',
          'Asegurar que el equipo pueda describir la visión en 5 minutos'
        ],
        tools: ['Vision Canvas', 'Elevator Pitch', 'Strategic Roadmap'],
        status: 'in_progress'
      },
      {
        step: 4,
        title: 'Comunicar la Visión',
        description: 'Comunicar frecuentemente la visión de manera poderosa',
        icon: 'MessageSquare',
        actions: [
          'Hablar de la visión en cada oportunidad',
          'Usar la visión para tomar decisiones diarias',
          'Responder abiertamente a preocupaciones',
          'Liderar con el ejemplo'
        ],
        tools: ['Plan de Comunicación', 'Townhalls', 'Newsletter'],
        status: 'pending'
      }
    ],
    adkarFocus: {
      letter: 'D',
      name: 'Desire',
      nameEs: 'Deseo',
      description: 'Generar el deseo de participar y apoyar el cambio',
      keyQuestion: '¿Qué gano yo con este cambio?',
      activities: [
        'Mostrar beneficios personales y profesionales',
        'Crear espacios seguros para expresar dudas',
        'Involucrar a líderes informales',
        'Conectar el cambio con motivaciones individuales'
      ],
      metrics: ['Nivel de entusiasmo', 'Voluntarios para pilotos', 'Reducción de resistencia'],
      score: 72
    },
    progress: 55,
    duration: '2-3 semanas'
  },
  {
    id: 3,
    phase: 'Ejecución',
    description: 'Implementar el cambio con victorias tempranas',
    color: 'from-emerald-500 to-teal-500',
    kotterSteps: [
      {
        step: 5,
        title: 'Eliminar Obstáculos',
        description: 'Remover barreras y empoderar a las personas para actuar',
        icon: 'Cog',
        actions: [
          'Identificar resistencias al cambio',
          'Remover obstáculos estructurales',
          'Alinear sistemas y estructuras con la visión',
          'Reconocer y recompensar a agentes de cambio'
        ],
        tools: ['Análisis de Barreras', 'Force Field Analysis', 'Reward System'],
        status: 'pending'
      },
      {
        step: 6,
        title: 'Crear Victorias Cortas',
        description: 'Planificar y crear mejoras visibles a corto plazo',
        icon: 'Target',
        actions: [
          'Buscar proyectos de éxito seguro',
          'Elegir victorias que sean visibles',
          'Reconocer a quienes lograron las victorias',
          'Comunicar los éxitos ampliamente'
        ],
        tools: ['Quick Wins Canvas', 'Success Stories', 'Recognition Program'],
        status: 'pending'
      }
    ],
    adkarFocus: {
      letter: 'K',
      name: 'Knowledge',
      nameEs: 'Conocimiento',
      description: 'Proporcionar el conocimiento de cómo cambiar',
      keyQuestion: '¿Cómo hago el cambio?',
      activities: [
        'Capacitación en nuevas herramientas de IA',
        'Sesiones en Vivo de prompt engineering',
        'Mentorías y coaching 1:1',
        'Recursos de autoestudio disponibles'
      ],
      metrics: ['Horas de capacitación', 'Evaluaciones aprobadas', 'Uso de recursos'],
      score: 65
    },
    progress: 30,
    duration: '4-8 semanas'
  },
  {
    id: 4,
    phase: 'Sostenibilidad',
    description: 'Consolidar y anclar el cambio en la cultura',
    color: 'from-violet-500 to-purple-500',
    kotterSteps: [
      {
        step: 7,
        title: 'Consolidar Ganancias',
        description: 'Construir sobre el cambio y mantener el momentum',
        icon: 'TrendingUp',
        actions: [
          'Analizar qué salió bien y qué mejorar',
          'Establecer metas más ambiciosas',
          'Mantener frescura con nuevos agentes de cambio',
          'Continuar el ciclo de mejora'
        ],
        tools: ['Retrospectivas', 'OKRs', 'Continuous Improvement'],
        status: 'pending'
      },
      {
        step: 8,
        title: 'Anclar el Cambio',
        description: 'Integrar los cambios en la cultura organizacional',
        icon: 'Shield',
        actions: [
          'Incorporar cambios en la cultura',
          'Conectar comportamientos con éxito',
          'Desarrollar medios para asegurar continuidad',
          'Crear planes de sucesión para líderes de cambio'
        ],
        tools: ['Culture Audit', 'Behavior Standards', 'Leadership Pipeline'],
        status: 'pending'
      }
    ],
    adkarFocus: {
      letter: 'A+R',
      name: 'Ability + Reinforcement',
      nameEs: 'Habilidad + Refuerzo',
      description: 'Desarrollar habilidad y reforzar para sostener el cambio',
      keyQuestion: '¿Cómo mantengo el cambio?',
      activities: [
        'Práctica supervisada de nuevas habilidades',
        'Sistemas de reconocimiento y recompensa',
        'Feedback continuo y coaching',
        'Celebración de éxitos y aprendizajes'
      ],
      metrics: ['Competencia demostrada', 'Adopción sostenida', 'Net Promoter Score interno'],
      score: 52
    },
    progress: 15,
    duration: '4-12 semanas'
  }
];

// ADKAR Elements for reference (individual tracking)
export const adkarElements = [
  {
    letter: 'A',
    name: 'Awareness',
    nameEs: 'Conciencia',
    description: 'Conciencia de la necesidad del cambio',
    phase: 'Preparación',
    questions: [
      '¿Entiende por qué es necesario el cambio?',
      '¿Conoce los riesgos de no cambiar?',
      '¿Sabe qué se espera de él/ella?'
    ],
    score: 85,
    color: 'from-amber-500 to-orange-500'
  },
  {
    letter: 'D',
    name: 'Desire',
    nameEs: 'Deseo',
    description: 'Deseo de participar y apoyar el cambio',
    phase: 'Visión',
    questions: [
      '¿Quiere ser parte del cambio?',
      '¿Ve beneficios personales?',
      '¿Está motivado para cambiar?'
    ],
    score: 72,
    color: 'from-blue-500 to-cyan-500'
  },
  {
    letter: 'K',
    name: 'Knowledge',
    nameEs: 'Conocimiento',
    description: 'Conocimiento de cómo cambiar',
    phase: 'Ejecución',
    questions: [
      '¿Sabe qué hacer diferente?',
      '¿Ha recibido la capacitación necesaria?',
      '¿Tiene acceso a recursos de aprendizaje?'
    ],
    score: 65,
    color: 'from-emerald-500 to-teal-500'
  },
  {
    letter: 'A',
    name: 'Ability',
    nameEs: 'Habilidad',
    description: 'Habilidad para implementar el cambio',
    phase: 'Sostenibilidad',
    questions: [
      '¿Puede demostrar las nuevas habilidades?',
      '¿Tiene las herramientas necesarias?',
      '¿Se le da tiempo para practicar?'
    ],
    score: 58,
    color: 'from-violet-500 to-purple-500'
  },
  {
    letter: 'R',
    name: 'Reinforcement',
    nameEs: 'Refuerzo',
    description: 'Refuerzo para sostener el cambio',
    phase: 'Sostenibilidad',
    questions: [
      '¿Recibe reconocimiento por adoptar el cambio?',
      '¿Hay consecuencias por no cambiar?',
      '¿Se celebran los éxitos?'
    ],
    score: 45,
    color: 'from-purple-500 to-pink-500'
  }
];

// Task Classification for Role Redesign
export const taskCategories = [
  {
    category: 'Automatizable',
    description: 'Tareas que pueden ser completamente automatizadas por IA',
    color: 'bg-red-500/20 text-red-400 border-red-500/30',
    icon: 'Cog',
    examples: ['Entrada de datos', 'Generación de reportes básicos', 'Clasificación de emails', 'Scheduling']
  },
  {
    category: 'Aumentable',
    description: 'Tareas donde la IA potencia las capacidades humanas',
    color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    icon: 'Zap',
    examples: ['Análisis de datos complejos', 'Creación de contenido', 'Toma de decisiones', 'Investigación']
  },
  {
    category: 'Exclusivamente Humana',
    description: 'Tareas que requieren habilidades únicamente humanas',
    color: 'bg-green-500/20 text-green-400 border-green-500/30',
    icon: 'Heart',
    examples: ['Liderazgo de equipos', 'Negociación compleja', 'Creatividad estratégica', 'Empatía y cuidado']
  }
];

// Sample Roles for Redesign
export const sampleRoles = [
  {
    id: 1,
    currentRole: 'Analista de Datos',
    department: 'Operaciones',
    tasks: [
      { name: 'Extracción de datos de sistemas', category: 'Automatizable', timePercent: 25 },
      { name: 'Limpieza y preparación de datos', category: 'Automatizable', timePercent: 20 },
      { name: 'Análisis estadístico avanzado', category: 'Aumentable', timePercent: 30 },
      { name: 'Presentación de insights a stakeholders', category: 'Exclusivamente Humana', timePercent: 15 },
      { name: 'Definición de KPIs estratégicos', category: 'Exclusivamente Humana', timePercent: 10 }
    ],
    newRole: 'Data Strategist',
    newFocus: 'Interpretación estratégica y storytelling con datos'
  },
  {
    id: 2,
    currentRole: 'Especialista de Atención al Cliente',
    department: 'Servicio',
    tasks: [
      { name: 'Responder consultas frecuentes', category: 'Automatizable', timePercent: 40 },
      { name: 'Resolver problemas complejos', category: 'Aumentable', timePercent: 25 },
      { name: 'Gestión de escalaciones', category: 'Exclusivamente Humana', timePercent: 20 },
      { name: 'Feedback y mejora de procesos', category: 'Exclusivamente Humana', timePercent: 15 }
    ],
    newRole: 'Customer Experience Architect',
    newFocus: 'Diseño de experiencias y resolución de casos críticos'
  },
  {
    id: 3,
    currentRole: 'Coordinador de Marketing',
    department: 'Marketing',
    tasks: [
      { name: 'Programación de publicaciones', category: 'Automatizable', timePercent: 20 },
      { name: 'Creación de contenido básico', category: 'Aumentable', timePercent: 30 },
      { name: 'Análisis de campañas', category: 'Aumentable', timePercent: 25 },
      { name: 'Estrategia de marca', category: 'Exclusivamente Humana', timePercent: 25 }
    ],
    newRole: 'AI-Powered Marketing Strategist',
    newFocus: 'Estrategia de contenido y orquestación de IA'
  }
];

// Future Skills
export const futureSkills = [
  {
    category: 'Technical Skills',
    skills: [
      { name: 'Prompt Engineering', priority: 'Alta', current: 30, target: 80 },
      { name: 'AI Tool Orchestration', priority: 'Alta', current: 25, target: 75 },
      { name: 'Data Literacy', priority: 'Media', current: 45, target: 85 },
      { name: 'No-Code Automation', priority: 'Media', current: 35, target: 70 }
    ]
  },
  {
    category: 'Soft Skills',
    skills: [
      { name: 'Critical Thinking', priority: 'Alta', current: 55, target: 90 },
      { name: 'Adaptability', priority: 'Alta', current: 50, target: 85 },
      { name: 'Collaboration with AI', priority: 'Alta', current: 20, target: 80 },
      { name: 'Ethical Decision Making', priority: 'Media', current: 40, target: 75 }
    ]
  },
  {
    category: 'Leadership Skills',
    skills: [
      { name: 'Change Leadership', priority: 'Alta', current: 35, target: 80 },
      { name: 'Vision Communication', priority: 'Media', current: 45, target: 85 },
      { name: 'Inclusive Leadership', priority: 'Media', current: 50, target: 80 },
      { name: 'Strategic Thinking', priority: 'Alta', current: 40, target: 90 }
    ]
  }
];

// Communication Templates
export const communicationTemplates = [
  {
    id: 1,
    type: 'announcement',
    title: 'Anuncio de Cambio',
    description: 'Template para comunicar nuevas iniciativas de transformación',
    icon: 'MessageSquare',
    sections: ['Contexto', 'Qué cambia', 'Por qué', 'Beneficios', 'Próximos pasos', 'Recursos']
  },
  {
    id: 2,
    type: 'faq',
    title: 'FAQ Builder',
    description: 'Generador de preguntas frecuentes y respuestas',
    icon: 'HelpCircle',
    sections: ['Preguntas comunes', 'Objeciones', 'Mitos vs Realidad', 'Recursos adicionales']
  },
  {
    id: 3,
    type: 'stakeholder',
    title: 'Plan de Stakeholders',
    description: 'Mapeo y estrategia de comunicación por audiencia',
    icon: 'Users',
    sections: ['Identificación', 'Análisis de influencia', 'Estrategia', 'Mensajes clave']
  },
  {
    id: 4,
    type: 'progress',
    title: 'Update de Progreso',
    description: 'Comunicación periódica de avances',
    icon: 'TrendingUp',
    sections: ['Logros del período', 'Métricas clave', 'Próximos hitos', 'Reconocimientos']
  }
];

// Growth Mindset Resources
export const growthMindsetResources = [
  {
    id: 1,
    title: 'De Fijo a Crecimiento: El Primer Paso',
    type: 'video',
    duration: '8 min',
    category: 'Mindset',
    description: 'Introducción al concepto de mentalidad de crecimiento en el contexto de la IA',
    completed: true
  },
  {
    id: 2,
    title: 'Abrazando la Incertidumbre',
    type: 'article',
    duration: '5 min',
    category: 'Resiliencia',
    description: 'Estrategias para desarrollar comodidad con la ambigüedad',
    completed: true
  },
  {
    id: 3,
    title: 'El Arte de Aprender a Aprender',
    type: 'interactive',
    duration: '15 min',
    category: 'Aprendizaje',
    description: 'Ejercicio práctico para desarrollar habilidades de meta-aprendizaje',
    completed: false
  },
  {
    id: 4,
    title: 'Historias de Transformación',
    type: 'case_study',
    duration: '12 min',
    category: 'Inspiración',
    description: 'Casos reales de líderes que navegaron cambios tecnológicos exitosamente',
    completed: false
  },
  {
    id: 5,
    title: 'Feedback como Combustible',
    type: 'workshop',
    duration: '30 min',
    category: 'Desarrollo',
    description: 'Sesión en vivo sobre cómo dar y recibir feedback constructivo',
    completed: false
  },
  {
    id: 6,
    title: 'Resiliencia Digital',
    type: 'video',
    duration: '10 min',
    category: 'Resiliencia',
    description: 'Construyendo fortaleza mental en tiempos de cambio tecnológico',
    completed: false
  }
];

// Adoption Metrics
export const adoptionMetrics = {
  overall: 67,
  byArea: [
    { area: 'Tecnología', adoption: 85, trend: 'up' },
    { area: 'Operaciones', adoption: 72, trend: 'up' },
    { area: 'Marketing', adoption: 68, trend: 'stable' },
    { area: 'Finanzas', adoption: 55, trend: 'up' },
    { area: 'RRHH', adoption: 48, trend: 'down' },
    { area: 'Legal', adoption: 42, trend: 'stable' }
  ],
  timeline: [
    { month: 'Ene', value: 35 },
    { month: 'Feb', value: 42 },
    { month: 'Mar', value: 48 },
    { month: 'Abr', value: 55 },
    { month: 'May', value: 62 },
    { month: 'Jun', value: 67 }
  ],
  alerts: [
    { type: 'warning', message: 'RRHH muestra señales de resistencia - requiere intervención', area: 'RRHH' },
    { type: 'success', message: 'Tecnología superó objetivo de adopción del Q2', area: 'Tecnología' },
    { type: 'info', message: 'Marketing solicita más recursos de capacitación', area: 'Marketing' }
  ]
};
